/**
 * The Officer Portal's notification inbox + preferences, over gov-api's
 * `/notifications` routes.
 *
 * Both halves are deliberately in one module: the settings panel and the bell
 * are two views of the same server state, and a preference change that mutes a
 * category should visibly settle the bell on the next poll rather than leaving
 * two caches to disagree.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationInboxDto,
  NotificationPreferencesDto,
  UpdateNotificationPreferencesBody,
} from '@munifocus/contracts-common';
import { api } from './api-client';

export const notificationKeys = {
  inbox: ['notifications', 'inbox'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

export async function getInbox(): Promise<NotificationInboxDto> {
  return (await api.get<NotificationInboxDto>('/notifications/inbox')).data;
}

export async function markAllRead(): Promise<void> {
  await api.post('/notifications/inbox/read-all');
}

export async function markRead(id: string): Promise<void> {
  await api.post(`/notifications/inbox/${id}/read`);
}

export async function getPreferences(): Promise<NotificationPreferencesDto> {
  return (await api.get<NotificationPreferencesDto>('/notifications/preferences')).data;
}

export async function updatePreferences(
  body: UpdateNotificationPreferencesBody,
): Promise<NotificationPreferencesDto> {
  return (await api.put<NotificationPreferencesDto>('/notifications/preferences', body)).data;
}

export function useInbox() {
  return useQuery({
    queryKey: notificationKeys.inbox,
    queryFn: getInbox,
    // The unread badge has to go stale fast enough that an officer who leaves
    // the portal open all shift still notices a new assignment, without the
    // bell becoming a poller worth worrying about.
    refetchInterval: 60_000,
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.inbox }),
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.inbox }),
  });
}

export function useNotificationPreferences() {
  return useQuery({ queryKey: notificationKeys.preferences, queryFn: getPreferences });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    // The settings card reports each save itself.
    meta: { silent: true },
    mutationFn: updatePreferences,
    onSuccess: (result) => {
      queryClient.setQueryData(notificationKeys.preferences, result);
      // A muted category stops producing inbox rows; refetch so the bell agrees.
      queryClient.invalidateQueries({ queryKey: notificationKeys.inbox });
    },
  });
}
